import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VoiceInput.css';

// Web Speech API対応チェック
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const isSpeechSupported = !!SpeechRecognition;

/**
 * 音声認識入力コンポーネント
 * @param {Object} props
 * @param {function} props.onResult - 認識結果のコールバック (text: string)
 * @param {function} props.onInterim - 中間結果のコールバック (text: string)
 * @param {boolean} props.disabled - 無効化フラグ
 * @param {boolean} props.autoStart - 自動開始フラグ
 * @param {string} props.placeholder - プレースホルダー
 */
function VoiceInput({
  onResult,
  onInterim,
  disabled = false,
  autoStart = false,
  placeholder = '話してください...',
}) {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  const recognitionRef = useRef(null);
  const restartTimeoutRef = useRef(null);

  // 音声認識の初期化
  useEffect(() => {
    if (!isSpeechSupported) {
      setError('お使いのブラウザは音声認識に対応していません');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'ja-JP';
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      setError('');
    };

    recognition.onend = () => {
      setIsListening(false);
      // 自動再開（disabled でなければ）
      if (!disabled && autoStart) {
        restartTimeoutRef.current = setTimeout(() => {
          if (recognitionRef.current && !disabled) {
            try {
              recognitionRef.current.start();
            } catch (e) {
              // すでに開始中の場合
            }
          }
        }, 100);
      }
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      if (interim) {
        setInterimText(interim);
        onInterim?.(interim);
      }

      if (final) {
        setInterimText('');
        onResult?.(final);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      switch (event.error) {
        case 'not-allowed':
        case 'service-not-allowed':
          setPermissionDenied(true);
          setError('マイクの使用が許可されていません');
          break;
        case 'no-speech':
          // 無言の場合は無視
          break;
        case 'audio-capture':
          setError('マイクが見つかりません');
          break;
        case 'network':
          setError('ネットワークエラー');
          break;
        default:
          setError('音声認識エラー');
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // 停止中でない場合
        }
      }
    };
  }, [autoStart, disabled, onInterim, onResult]);

  // disabled 状態の変更を監視
  useEffect(() => {
    if (disabled && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // すでに停止中
      }
    }
  }, [disabled]);

  // 自動開始
  useEffect(() => {
    if (autoStart && !disabled && !isListening && recognitionRef.current && !permissionDenied) {
      try {
        recognitionRef.current.start();
      } catch (e) {
        // すでに開始中
      }
    }
  }, [autoStart, disabled, isListening, permissionDenied]);

  // 手動で開始/停止
  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
      } catch (e) {
        // すでに開始中
      }
    }
  }, [isListening]);

  // マイク許可を再要求
  const requestPermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionDenied(false);
      setError('');
      if (recognitionRef.current) {
        recognitionRef.current.start();
      }
    } catch (e) {
      setError('マイクへのアクセスが拒否されました');
    }
  }, []);

  if (!isSpeechSupported) {
    return (
      <div className="voice-input-container not-supported">
        <p className="voice-error">音声認識非対応</p>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <div className="voice-input-container permission-denied">
        <p className="voice-error">{error}</p>
        <button className="voice-permission-btn" onClick={requestPermission}>
          マイクを許可
        </button>
      </div>
    );
  }

  return (
    <div className={`voice-input-container ${isListening ? 'listening' : ''} ${disabled ? 'disabled' : ''}`}>
      {/* マイクボタン */}
      <button
        type="button"
        className={`voice-btn ${isListening ? 'active' : ''}`}
        onClick={toggleListening}
        disabled={disabled}
        aria-label={isListening ? '音声認識を停止' : '音声認識を開始'}
      >
        <span className="voice-icon">🎤</span>
        {isListening && <span className="voice-pulse"></span>}
      </button>

      {/* 認識中のテキスト */}
      <div className="voice-display">
        {isListening ? (
          interimText ? (
            <span className="voice-interim">{interimText}</span>
          ) : (
            <span className="voice-placeholder">{placeholder}</span>
          )
        ) : (
          <span className="voice-hint">タップして話す</span>
        )}
      </div>

      {/* エラー表示 */}
      {error && !permissionDenied && (
        <p className="voice-error-inline">{error}</p>
      )}
    </div>
  );
}

/**
 * 音声認識が利用可能かチェック
 */
export function isVoiceInputSupported() {
  return isSpeechSupported;
}

export default VoiceInput;
